import { Navigate } from 'react-router-dom';
import { useAuthStore } from '@shared/state';
import { useEffect, useState } from 'react';
import { doc, getDoc, getFirestore } from 'firebase/firestore';
import { app } from '@shared/services/firebase';

interface Props {
  children: React.ReactNode;
}

export function AdminRoute({ children }: Props) {
  const user = useAuthStore((s) => s.user);
  const authLoading = useAuthStore((s) => s.loading);
  // Track which uid the role was last verified for. The effect-based reset
  // pattern races on refresh: when authLoading flips false and user becomes
  // non-null in the same render, the previous render's roleChecked=true
  // (set when user was null) leaks through and redirects to /dashboard
  // before the effect can re-fetch.
  const [verifiedFor, setVerifiedFor] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (!user) {
      // Authenticated check is done — there's no user, nothing to verify.
      setVerifiedFor('__no-user__');
      setIsAdmin(false);
      return;
    }
    let cancelled = false;
    (async () => {
      const snap = await getDoc(doc(getFirestore(app), 'users', user.uid));
      if (cancelled) return;
      setIsAdmin(snap.data()?.role === 'admin');
      setVerifiedFor(user.uid);
    })();
    return () => { cancelled = true; };
  }, [user]);

  // While auth is loading, OR the role hasn't been verified for the CURRENT
  // user yet, hold on the spinner. Comparing verifiedFor === user.uid
  // prevents the flash redirect on refresh.
  const verifiedForCurrentUser =
    user ? verifiedFor === user.uid : verifiedFor === '__no-user__';
  if (authLoading || !verifiedForCurrentUser) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-dark-bg">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) return <Navigate to="/admin-login" replace />;
  if (!isAdmin) return <Navigate to="/dashboard" replace />;

  return <>{children}</>;
}
